'use client';

import ArticlePagination from '@/components/articlePagination/ArticlePagination';
import ArticleStage from '@/components/articleStage/ArticleStage';
import HeadlineField from '@/components/customFields/HeadlineField';
import { type GenericBlok, HeadlineTag } from '@/components/customFields/types';
import NestedBlocks from '@/components/nestedBlocks/NestedBlocks';
import Pill from '@/components/pill/Pill';
import { type PillCardProps } from '@/components/pillCard/PillCard';
import { RichText } from '@/components/richText/RichText';
import {
  SwdAccordionHeader,
  SwdAccordionItem,
  SwdAccordionPanel,
  SwdAccordionRoot,
  SwdAccordionTrigger,
} from '@/components/swdAccordion/SwdAccordion';
import type { TextCardProps } from '@/components/textCard/TextCard';
import TextCards from '@/components/textCards/TextCards';
import { getFormattedDMYDate, getFormattedDate } from '@/helper/dates';
import tracking, { EventSource, type TrackingInformation } from '@/helper/tracking';
import { resolveStory } from '@/helper/utils';
import { getTrackingItemFromVTWPage, getVtwReplacementTexts } from '@/helper/vtwutils';
import { useUserStore } from '@/providers/UserStoreProvider';
import type { UserState } from '@/stores/UserStore';
import type { EntityVtwCategoryStoryblok, PageVtwBenefitStoryblok } from '@/types/component-types-sb';
import type { ISbStoryData } from '@storyblok/react/rsc';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import BenefitBox from './BenefitBox';

export type PageVtwBenefitProps = {
  story: ISbStoryData<PageVtwBenefitStoryblok>;
  breadcrumbs: ISbStoryData<GenericBlok>[];
  latestArticles?: PillCardProps[];
};

const PageVtwBenefit: React.FC<PageVtwBenefitProps> = ({ story, breadcrumbs, latestArticles }) => {
  const { t } = useTranslation();

  const { name, setTrackingData, trackingData, hydrated, loggedIn } = useUserStore((state) => ({
    getUserState: state.getUserState,
    setTrackingData: state.setTrackingData,
    name: state.name,
    loggedIn: state.loggedIn,
    hydrated: state.hydrated,
    trackingData: state.trackingData,
  }));

  const [viewItemTracked, setViewItemTracked] = useState<boolean>(false);

  const blok = story.content;
  const validFromDmy = getFormattedDMYDate(blok.valid_from);
  const validToDmy = getFormattedDMYDate(blok.valid_to);

  const vtwReplacementTexts = getVtwReplacementTexts(
    { loggedIn, name } as UserState,
    validFromDmy,
    validToDmy,
    blok.partner_link?.url
  );

  const textCards: TextCardProps[] = [];

  if (blok.box_instructions) {
    const instructionCard: TextCardProps = {
      heading: t('vtw.instructionCardHeadline'),
      text: blok.box_instructions,
    };

    textCards.push(instructionCard);
  }

  if (blok.box_information) {
    const informationCard: TextCardProps = {
      heading: t('vtw.informationCardHeadline'),
      text: blok.box_information,
      replace: vtwReplacementTexts,
    };

    textCards.push(informationCard);
  }

  if (blok.box_partner) {
    const partnerCard: TextCardProps = {
      heading: t('vtw.partnerCardHeadline'),
      text: blok.box_partner,
      replace: vtwReplacementTexts,
    };

    textCards.push(partnerCard);
  }

  useEffect(() => {
    if (hydrated) return;
    // track view item
    if (trackingData) {
      //tracking data does not match - remove from store
      if (trackingData?.itemId !== blok._uid) {
        const trackingInformation: TrackingInformation = getTrackingItemFromVTWPage(
          blok,
          EventSource.VorteilsweltCurrentOffers,
          0
        );
        setTrackingData(trackingInformation);
      } else if (!viewItemTracked) {
        tracking.nonCommodityOrBenefitViewItem([trackingData]);
        setViewItemTracked(true);
      }
    }
  }, [hydrated, viewItemTracked, blok]);

  const renderStageTags = () => {
    return (
      blok.category?.map((category, index) => {
        const tag = resolveStory<EntityVtwCategoryStoryblok>(category);
        return tag && <Pill key={index} label={tag.name} color={tag.content.color.name} />;
      }) ?? []
    );
  };

  const stageFooter = <div>{t('vtw.validTo', { date: getFormattedDate(blok.valid_to) })}</div>;

  return (
    <div data-testid="vtw-page-benefits">
      {blok && (
        <ArticleStage
          article={blok}
          breadcrumbs={breadcrumbs}
          footerContent={stageFooter}
          linkLastElementInBreadcrumb={true}
          pills={renderStageTags()}
        />
      )}
      <div className="grid-wrapper">
        <div className="content-bloks-wrapper">
          <BenefitBox story={story} replacements={vtwReplacementTexts} />

          <NestedBlocks blocks={blok.content} />

          {textCards.length > 0 && <TextCards cards={textCards} />}

          <SwdAccordionRoot variant="standard" defaultValue={[]}>
            <SwdAccordionItem value="terms">
              <SwdAccordionHeader>
                <SwdAccordionTrigger>{t('vtw.terms')}</SwdAccordionTrigger>
              </SwdAccordionHeader>
              <SwdAccordionPanel>
                <RichText text={blok.terms} replace={vtwReplacementTexts} />
              </SwdAccordionPanel>
            </SwdAccordionItem>
          </SwdAccordionRoot>

          {latestArticles && latestArticles.length > 0 && (
            <section className="article-more">
              <HeadlineField tag={HeadlineTag.H2} style={HeadlineTag.H3}>
                {t('vtw.moreBenefits')}
              </HeadlineField>
              <ArticlePagination baseUrl="/vorteilswelt/" articles={latestArticles} />
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default PageVtwBenefit;
